import { datadogRum } from "@datadog/browser-rum";
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Button,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ReactElement, useEffect } from "react";
import { FormattedMessage } from "react-intl";

import { useIntl } from "~frontend/providers/IntlProvider";
import theme from "~frontend/theme";
import { spacingArray } from "~frontend/util/styling";

import { getApolloErrorMessage } from "../util/errors";
import { ExternalLink, InternalLink } from "./Link";

const Root = styled("div")(({ theme }) => ({
  margin: theme.spacing(2),
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
}));

const ErrorCardInner = styled(Card)({
  maxWidth: "600px",
  width: "80%",
});

export default function ErrorCard({
  errors,
  skipApollo = false,
  onClickReload,
}: {
  errors: any[];
  skipApollo?: boolean;
  onClickReload?: () => void | Promise<void>;
}): ReactElement {
  const intl = useIntl();

  // log error for tracking, but only on the first render or when the errors change
  useEffect(() => {
    datadogRum.addError(errors[0]);
  }, [errors]);

  return (
    <Root>
      <ErrorCardInner>
        <CardHeader
          title={
            <FormattedMessage
              defaultMessage="Encountered errors"
              description="Title for error message"
            />
          }
        />
        <CardContent
          sx={{
            display: "flex",
            gap: theme.spacing(1),
            flexDirection: "column",
          }}
        >
          {errors.map((err, index) => (
            <Box key={index}>
              {skipApollo ? err : getApolloErrorMessage(err, intl)}
            </Box>
          ))}
          <Box>
            <FormattedMessage
              defaultMessage="Seeing this a lot?"
              description="Intro text before link to submit a bug report"
            />{" "}
            <ExternalLink href="https://support.scaletowin.com/contact">
              <FormattedMessage
                defaultMessage="Send us a support ticket."
                description="Link to submit a bug report"
              />
            </ExternalLink>
          </Box>
        </CardContent>
        <CardActions
          sx={{
            display: "flex",
            gap: theme.spacing(2),
            padding: spacingArray([0, 2, 2]),
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={() =>
              onClickReload ? onClickReload() : window.location.reload()
            }
          >
            <FormattedMessage
              defaultMessage="Reload"
              description="Button to refresh the page"
            />
          </Button>
          <InternalLink to={"/"}>
            <FormattedMessage
              defaultMessage="Go Home"
              description="Button to go to the home page"
            />
          </InternalLink>
        </CardActions>
      </ErrorCardInner>
    </Root>
  );
}
